// import { ValiError } from 'valibot';

import { API } from '@/api';
import { fetcher, HTTPError } from '@/services/fetcher';
import { toURLSearchParams } from '@/utils/url';

import {
  type AccessorialDetailsParams,
  type AccessorialsFiltersApi,
  accessorialsApiSchema,
  type TariffApi,
  type TariffDetailsParams,
  type TariffPricingApi,
  tariffApiSchema,
  tariffDetailsApiSchema,
  tariffPricingApiSchema,
} from '../models';
import { logger } from '../utils/logger';
import * as mock from './mock';

const pricing = fetcher.extend(() => ({ prefixUrl: `${API.BASE_PRICING}` }));
const accessorial = fetcher.extend(() => ({ prefixUrl: API.ACCESSORIALS }));

const tariffDetails = fetcher.extend(() => ({
  prefixUrl: `${API.BASE_PRICING}/tariff-detail`,
  retry: 0,
}));

export const fetchTariff = async (
  { tariffKey }: { tariffKey: number },
  { signal }: { signal?: AbortController['signal'] } = {},
): Promise<TariffApi> => {
  try {
    return await pricing
      .get(`tariffs/${tariffKey}`, {
        signal,
      })
      .json(tariffApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Tariff error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchTariffPricing = async (
  { tariffKey }: { tariffKey: number },
  { signal }: { signal?: AbortController['signal'] } = {},
): Promise<TariffPricingApi> => {
  try {
    return await pricing
      .get(`pricing/${tariffKey}/$get-all-by-tariff`, {
        signal,
      })
      .json(tariffPricingApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Tariff pricing error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchTariffDetails = async (
  params: TariffDetailsParams,
  { signal }: { signal?: AbortController['signal'] } = {},
) => {
  try {
    const {
      accountNumber,
      charge,
      charge2,
      class: freightClass,
      cost,
      customerLaneId,
      customerNotes,
      dimDivisor,
      distanceFrom,
      distanceTo,
      distanceType,
      effectiveDateFrom,
      effectiveDateTo,
      laneCode,
      laneKey,
      limit = 10,
      liner,
      maximumCharge,
      minimumCharge,
      offset = 0,
      pricingBasis,
      pricingBasis2,
      productCode,
      shipmentType,
      sorting,
      tariffDetailKey,
      tariffKey,
      unitsFrom,
      unitsTo,
      unitType,
      weightFrom,
      weightTo,
      weightType,
    } = params;

    return await tariffDetails
      .get('search', {
        searchParams: toURLSearchParams({
          AccountNumber: accountNumber || undefined,
          Charge: charge,
          Charge2: charge2,
          Class: freightClass || undefined,
          Cost: cost,
          CustomerLaneId: customerLaneId || undefined,
          CustomerNotes: customerNotes || undefined,
          DimDivisor: dimDivisor,
          DistanceFrom: distanceFrom,
          DistanceTo: distanceTo,
          DistanceType: distanceType || undefined,
          EffectiveDateFrom: effectiveDateFrom || undefined,
          EffectiveDateTo: effectiveDateTo || undefined,
          LaneCode: laneCode || undefined,
          LaneKey: laneKey,
          Liner: liner || undefined,
          MaximumCharge: maximumCharge,
          MinimumCharge: minimumCharge,
          PricingBasis: pricingBasis || undefined,
          PricingBasis2: pricingBasis2 || undefined,
          ProductCode: productCode || undefined,
          ShipmentType: shipmentType || undefined,
          Skip: offset,
          Sorting: sorting || undefined,
          Take: limit,
          TariffDetailKey: tariffDetailKey,
          TariffKey: tariffKey,
          UnitsFrom: unitsFrom,
          UnitsTo: unitsTo,
          UnitType: unitType || undefined,
          WeightFrom: weightFrom,
          WeightTo: weightTo,
          WeightType: weightType || undefined,
        }),
        signal,
      })
      .json(tariffDetailsApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Tariff details error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchAccessorialDetails = (
  params: AccessorialDetailsParams,
  _options: { signal?: AbortController['signal'] } = {},
) => mock.fetchAccessorialDetails(params, _options);

export const fetchAccessorials = async (
  params: AccessorialsFiltersApi,
  { signal }: { signal?: AbortController['signal'] } = {},
) => {
  try {
    return await accessorial
      .get('search', { searchParams: toURLSearchParams(params), signal })
      .json(accessorialsApiSchema);
  } catch (error) {
    if (error instanceof HTTPError) {
      logger.error(`Accessorials error: ${error.response.status}`);
    }
    throw error;
  }
};

export const fetchSortingOptions = (options: { signal?: AbortController['signal'] } = {}) =>
  mock.fetchSortingOptions(options);
